import React from 'react';

import { ApolloClient, HttpLink, InMemoryCache } from '@apollo/client';
import { SetContextLink } from '@apollo/client/link/context';
import { RemoveTypenameFromVariablesLink } from '@apollo/client/link/remove-typename';
import { ApolloProvider } from '@apollo/client/react';

import { GRAPHQL_URI } from '@/utils/env';
import i18n from '@/utils/i18n';

export const httpLink = new HttpLink({
  uri: GRAPHQL_URI,
});

let authToken = '';
let currentLanguage = i18n.language || 'en';

i18n.on('languageChanged', (lng: string) => {
  currentLanguage = lng;
});

export const setAuthToken = (token: string) => {
  authToken = token;
};

const authLink = new SetContextLink((previousContext) => {
  const headers =
    (previousContext?.headers as Record<string, string> | undefined) ?? {};

  return {
    headers: {
      ...headers,
      authorization: authToken ? `Bearer ${authToken}` : '',
      'accept-language': currentLanguage || 'en',
    } as { [name: string]: string },
  };
});

const removeTypenameLink = new RemoveTypenameFromVariablesLink();

const apolloClient = new ApolloClient({
  link: removeTypenameLink.concat(authLink).concat(httpLink),
  devtools: {
    // v8 ignore next
    enabled: import.meta.env.DEV,
  },
  cache: new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          devicesByBuildings: {
            keyArgs: ['deviceId'],
            merge: (existing, incoming, { args }) => {
              const skip = args?.skip || 0;
              if (skip === 0) {
                return [...incoming];
              }
              return [...existing, ...incoming];
            },
          },
        },
      },
    },
  }),
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'no-cache',
      errorPolicy: 'ignore',
    },
    query: {
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
    },
  },
});

interface GraphQLProviderProps {
  children?: React.ReactNode;
}

export const GraphQLProvider: React.FC<GraphQLProviderProps> = ({
  children,
}) => <ApolloProvider client={apolloClient}>{children}</ApolloProvider>;
